import mixpanel from 'mixpanel-browser';
import type { AnalyticsService } from './AnalyticsService';


export class MixpanelAnalyticsService implements AnalyticsService {
  private readonly mixpanelToken: string | undefined = import.meta.env.VITE_MIXPANEL_TOKEN

  constructor() {
    if (this.mixpanelToken) {
      try {
        const trackPageview = false; // Page views will be tracked manually
        const recordSessionsPercent = 100;
        mixpanel.init(this.mixpanelToken, {
          debug: false, // Disable debug to reduce noise from blocked requests
           
          track_pageview: trackPageview,
          persistence: 'localStorage',
          autocapture: false,
           
          record_sessions_percent: recordSessionsPercent,
        });

      } catch (error) {
        console.warn('Mixpanel initialization failed (possibly blocked by ad blocker):', error);
      }
    } else {
      console.warn(
        "Mixpanel token not found. Analytics will be disabled. " +
        "Note: Ad blockers can also prevent analytics from working."
      );
    }
  }

  private _executeMixpanelOperation(
    operation: (name: string, props?: Record<string, unknown>) => void,
    logMessage: string,
    name: string,
    props?: Record<string, unknown>
  ): void {
    if (this.mixpanelToken) {
      try {
        operation(name, props);
      } catch (error) {
        console.error(`Mixpanel: Failed to ${logMessage.toLowerCase()}:`, error, name, props);
      }
    }
  }

  track(eventName: string, properties?: Record<string, unknown>): void {
    this._executeMixpanelOperation(
      (name, props) => mixpanel.track(name, props),
      `Tracking event "${eventName}" with properties:`,
      eventName,
      properties
    );
  }

  trackPageView(path: string): void {
    this._executeMixpanelOperation(
      (name, props) => mixpanel.track(name, props),
      `Tracking page view for path "${path}"`,
      'Page View',
      { path }
    );
  }

  setUser(userId: string, properties?: Record<string, unknown>): void {
    this._executeMixpanelOperation(
      (name) => {
        mixpanel.identify(name);
        if (properties) {
          mixpanel.people.set(properties);
        }
      },
      `Identifying user "${userId}" with properties:`,
      userId,
      properties
    );
  }

  reset(): void {
    this._executeMixpanelOperation(
      () => mixpanel.reset(),
      'Resetting user identity',
      'reset'
    );
  }
}
